import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { XMarkIcon, ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { Input, Checkbox } from "./ui";
import PhoneInput from "./PhoneInput";

const SortableItem = ({
  recipient,
  index,
  isSequential,
  isOnlySigner,
  onRemoveRecipient,
  onRecipientsChange,
  onCCChange,
  getRecipientError,
  isRecipientTouched,
  allRecipients,
  formik,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `recipient-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleBlur = (field) => {
    if (typeof isRecipientTouched === "function") {
      isRecipientTouched(index, field);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-4 p-4 border rounded-lg relative ${
        isDragging ? "shadow-lg" : ""
      }`}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${recipient.color}`}
      ></div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {!recipient.isCC && isSequential && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing"
            >
              <ArrowsUpDownIcon className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <span className="text-sm font-medium text-gray-700">
            {!recipient.isCC && isSequential
              ? `Signer ${index + 1}`
              : `Recipient ${index + 1}`}
          </span>
        </div>
        {index > 0 && (
          <button
            type="button"
            onClick={() => onRemoveRecipient(index)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Name"
          id={`name-${index}`}
          value={recipient.name}
          onChange={(e) => {
            const updatedRecipients = [...allRecipients];
            updatedRecipients[index].name = e.target.value;
            onRecipientsChange(updatedRecipients);
          }}
          onBlur={() => handleBlur("name")}
          placeholder="Name"
          size="default"
          error={
            formik?.touched?.recipients?.[index]?.["name"] &&
            getRecipientError(index, "name")
              ? getRecipientError(index, "name")
              : undefined
          }
        />
        <Input
          label="Role"
          id={`role-${index}`}
          value={recipient.role}
          onChange={(e) => {
            const updatedRecipients = [...allRecipients];
            updatedRecipients[index].role = e.target.value;
            onRecipientsChange(updatedRecipients);
          }}
          onBlur={() => handleBlur("role")}
          placeholder="Role"
          size="default"
          error={
            formik?.touched?.recipients?.[index]?.["role"] &&
            getRecipientError(index, "role")
              ? getRecipientError(index, "role")
              : undefined
          }
        />
        <Input
          label="Email"
          id={`email-${index}`}
          type="email"
          value={recipient.email}
          onChange={(e) => {
            const updatedRecipients = [...allRecipients];
            updatedRecipients[index].email = e.target.value;
            onRecipientsChange(updatedRecipients);
          }}
          onBlur={() => handleBlur("email")}
          placeholder="Email"
          size="default"
          error={
            formik?.touched?.recipients?.[index]?.["email"] &&
            getRecipientError(index, "email")
              ? getRecipientError(index, "email")
              : undefined
          }
        />
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Phone Number
          </label>
          <PhoneInput
            id={`phone-${index}`}
            value={recipient.phone || ""}
            onChange={(value) => {
              const updatedRecipients = [...allRecipients];
              updatedRecipients[index].phone = value;
              onRecipientsChange(updatedRecipients);
            }}
            onBlur={() => handleBlur("phone")}
            placeholder="Phone number"
            error={
              formik?.touched?.recipients?.[index]?.["phone"] &&
              getRecipientError(index, "phone")
                ? getRecipientError(index, "phone")
                : undefined
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional, required for OTP verification
          </p>
        </div>
      </div>

      {index > 0 && (
        <div className="mt-3">
          <Checkbox
            id={`cc-${index}`}
            checked={recipient.isCC}
            onChange={(e) => onCCChange(index, e)}
            label="CC Recipient"
          />
        </div>
      )}
    </div>
  );
};

const DraggableRecipientsList = ({
  recipients = [],
  isSequential = false,
  isOnlySigner = false,
  onRecipientsChange = () => {},
  onRemoveRecipient = () => {},
  onCCChange = () => {},
  getRecipientError = () => null,
  isRecipientTouched = () => false,
  formik = null,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || !isSequential) return;

    const oldIndex = parseInt(active.id.split("-")[1]);
    const newIndex = parseInt(over.id.split("-")[1]);
    if (oldIndex !== newIndex) {
      const updatedRecipients = arrayMove(recipients, oldIndex, newIndex);

      // Update sequences based on new order
      const nonCCRecipients = updatedRecipients
        .filter((r) => !r.isCC)
        .map((recipient, index) => ({
          ...recipient,
          sequence: index + 1,
        }));

      const ccRecipients = updatedRecipients.filter((r) => r.isCC);
      const sortedRecipients = [...nonCCRecipients, ...ccRecipients];

      onRecipientsChange(sortedRecipients);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={recipients.map((_, index) => `recipient-${index}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-4">
          {recipients.map((recipient, index) => (
            <SortableItem
              key={`recipient-${index}`}
              recipient={recipient}
              index={index}
              isSequential={isSequential}
              isOnlySigner={isOnlySigner}
              onRemoveRecipient={onRemoveRecipient}
              onRecipientsChange={onRecipientsChange}
              onCCChange={onCCChange}
              getRecipientError={getRecipientError}
              isRecipientTouched={isRecipientTouched}
              allRecipients={recipients}
              formik={formik}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableRecipientsList;
